import { useSelector } from 'react-redux';
import { useGetMembersWithExtensionsQuery } from '../services/VoipService';
import { getLoggedInUser, getLoggedInUserOrganization } from '../selectors/userSelectors';
import { isCCR } from '../helpers/UserHelpers';

/**
 * Resolves whether the logged-in member is a VoIP user.
 *
 * The Redux `state.user.users[loggedInId].extensions` array is unreliable for
 * this gate — minimal user payloads from group/role list endpoints overwrite
 * it, which causes the phone icon (and other VoIP UI) to disappear until
 * refresh. The members-with-extensions endpoint lives in its own RTK Query
 * cache and is not contaminated by those reducers, so we use it as the
 * authoritative source.
 *
 * BUG-012: CCRs never have a valid extension, so the Recents/Voicemail page
 * can't function for them. Treat them as non-VoIP users so the phone icon
 * and VoIP page are hidden, matching the "Assigned to Me" inbox treatment.
 */
export default function useIsVoipUser() {
  const currentUser = useSelector(getLoggedInUser);
  const organization = useSelector(getLoggedInUserOrganization);
  const isVoipEnabled = !!organization?.isVoipEnabled;
  const isCcr = isCCR(currentUser);
  const skipQuery = !isVoipEnabled || isCcr;
  const { data } = useGetMembersWithExtensionsQuery(undefined, { skip: skipQuery });
  const members = Array.isArray(data?.objects) ? data.objects : [];
  const hasExtension = !isCcr
    && currentUser?.id != null
    && members.some((m) => Number(m.userId) === Number(currentUser.id) && !!m.extension);

  return {
    isVoipEnabled,
    hasExtension,
    isVoipUser: isVoipEnabled && hasExtension,
  };
}
